import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { DeferredToaster } from "@/components/ui/sonner-deferred";
import appCss from "../styles.css?url";
import { DEFAULT_OG_DESCRIPTION, DEFAULT_OG_TITLE, DEFAULT_SOCIAL_META, LOCAL_GEO_META, SITE_URL } from "@/lib/seo";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-gradient-primary">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist.
        </p>
        <div className="mt-6">
          <Link to="/" className="inline-flex items-center justify-center rounded-md bg-gradient-primary px-4 py-2 text-sm font-medium text-primary-foreground">
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#000000" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "BLVDS" },
      { title: DEFAULT_OG_TITLE },
      { name: "description", content: DEFAULT_OG_DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "BLVDS Detailing" },
      ...LOCAL_GEO_META,
      ...DEFAULT_SOCIAL_META,
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "shortcut icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      // Resource hints for external origins (dns-prefetch is lower-cost than preconnect
      // and avoids Lighthouse "unused preconnect" warnings since fonts load deferred via print-swap)
      { rel: "dns-prefetch", href: "https://fonts.googleapis.com" },
      { rel: "dns-prefetch", href: "https://fonts.gstatic.com" },
      { rel: "dns-prefetch", href: "https://www.google.com" },
      { rel: "dns-prefetch", href: "https://maps.gstatic.com" },
      // Non-blocking font load with display=swap to prevent render blocking (FOUT instead of FOIT)
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Oswald:ital,wght@0,500;0,600;0,700;1,500;1,600;1,700&family=Inter:wght@400;500;600&display=swap",
        media: "print",
        onLoad: "this.media='all'",
      } as never,
    ],
  }),
  shellComponent: RootShell,
  component: () => <Outlet />,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="dark">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <DeferredToaster />
        <Scripts />
      </body>
    </html>
  );
}
